import { LinkSection, LinkSubsection } from '../types/LinkSection'

interface Box {
  rootNode: LinkSection
  size: number
}

interface Column {
  boxes: Box[]
  size: number
}

/**
 * Divides boxes into roughly equal columns based on estimated box size.
 *
 * Rationale for not using CSS columns (coloumn-count): the vastly
 * different heights of the boxes, combined with how few they are, causes
 * the column rendering to display too few columns without option to center
 * the content.
 * @param maxNumColumns the desired (max) number of columns
 * @param boxes footer menu boxes to distribute in columns
 * @param trimEmptyColumns set to true to remove columns that don't have any boxes
 * @returns columns with boxes
 */
export const fitBoxesInColumns = (
  maxNumColumns: number,
  boxes: Box[],
  trimEmptyColumns = false
): Column[] => {
  if (maxNumColumns < 1) {
    throw Error('Must be at least 1 column')
  }

  const totalSum = boxes.reduce((sum, box) => sum + box.size, 0)

  // adjust marginOfError to allow slightly more than the average
  // column size in early columns to avoid having too many columns
  // left in the last columns. It's an estimation to avoid additional loops.
  const marginOfError = 1.2
  const colSize = Math.ceil((totalSum / maxNumColumns) * marginOfError)

  // create columns
  const cols: Column[] = Array.from({ length: maxNumColumns }, _ => ({
    boxes: [],
    size: 0,
  }))

  let colIndex = 0
  let currentColumn = cols[colIndex]
  let boxesLeft = boxes.length
  boxes.forEach(box => {
    const colHasBoxes = currentColumn.size > 0
    const fewBoxesLeft = cols.length - colIndex > boxesLeft
    const notLastColumn = colIndex < cols.length - 1
    if (
      colHasBoxes &&
      notLastColumn &&
      (fewBoxesLeft || currentColumn.size + box.size > colSize)
    ) {
      colIndex++
      currentColumn = cols[colIndex]
    }

    currentColumn.boxes.push(box)
    currentColumn.size += box.size
    boxesLeft--
  })

  if (trimEmptyColumns) {
    return cols.filter(col => col.size > 0)
  } else {
    return cols
  }
}

/**
 * Get the estimated size of all items and sub items in a footer root node
 * @param boxes footer root nodes (link boxes) to estimate the size of
 * @returns Array of root nodes wrapped in a box that keeps the estimated size of the root node
 */
export const getSizedBoxes = (boxes: LinkSection[]): Box[] =>
  boxes.map(box => ({
    rootNode: box,
    size: calculateBoxSize(box),
  }))

const calculateBoxSize = (box: LinkSection): number => {
  const boxHeaderSize = 4
  let size = boxHeaderSize
  box.subitems?.forEach(item => {
    const subsection = item as LinkSubsection
    if (subsection?.links) {
      size += calculateGroupSize(subsection)
    } else {
      size += 1
    }
  })

  return size
}

const calculateGroupSize = (group: LinkSubsection): number => {
  const groupHeaderSize = 2
  const numItems = group.links ? group.links.length : 0
  return groupHeaderSize + numItems
}
